import type { Command, FillCellValue } from "../document/commands.ts";
import { packedToTileRef, sameTileRef } from "../document/packed-tile.ts";
import type { LayerDocument, TilesetRef, WorldDocument } from "../document/types.ts";
import { zoneTypeAt } from "../document/zones.ts";
import { doorAt } from "../document/doors.ts";
import { layerOf } from "./resolve-types.ts";

/**
 * The part of a cell command that can still be taken back (ADR-086 decision 27): a cell goes back to what it
 * held before the edit only where it still holds what the edit left; where someone has changed it since, their
 * value stays. Values are compared by meaning: a tile by the tileset and tile it names, a zone cell by its
 * type id, a door cell by its door's id.
 */

/**
 * A command over a layer's cells.
 */
export type CellCommand = Extract<Command, { readonly kind: "paintCorners" | "paintCollision" | "paintZone" | "paintDoor" | "setTiles" | "fillCells" }>;

/**
 * A cell command narrowed to what still holds: the inverse over those cells, or null when none does, with how
 * many cells it takes back, how many it leaves because someone changed them, and how many it leaves because
 * their layer is gone or holds another kind of cell now.
 */
export interface NarrowedCells {
  readonly command: Command | null;
  readonly kept: number;
  readonly skipped: number;
  readonly gone: number;
}

/**
 * How to ask whether a cell of a layer holds a value, or null when the layer holds no cells of the kind the
 * command writes.
 *
 * @param layer The layer.
 * @param tilesets The world's tilesets, which a tile cell is read against.
 * @param kind The command's kind.
 * @returns The test, false for an index outside the grid or a cell that cannot be read.
 */
function holder(layer: LayerDocument | undefined, tilesets: readonly TilesetRef[], kind: CellCommand["kind"]): ((index: number, value: FillCellValue) => boolean) | null {
  const fill = kind === "fillCells";
  if (layer?.kind === "terrain" && (fill || kind === "paintCorners")) {
    return (index, value) => index < layer.corners.length && layer.corners[index] === value;
  }
  if (layer?.kind === "collision" && (fill || kind === "paintCollision")) {
    return (index, value) => index < layer.cells.length && layer.cells[index] === value;
  }
  if (layer?.kind === "zone" && (fill || kind === "paintZone")) {
    return (index, value) => index < layer.cells.length && zoneTypeAt(layer, index) === value;
  }
  if (layer?.kind === "door" && (fill || kind === "paintDoor")) {
    return (index, value) => index < layer.cells.length && (doorAt(layer, index)?.id ?? null) === value;
  }
  if (layer?.kind === "tile" && (fill || kind === "setTiles")) {
    return (index, value) => {
      if (index >= layer.tiles.length || typeof value === "number" || typeof value === "string") {
        return false;
      }
      try {
        return sameTileRef(packedToTileRef(layer.tiles[index] as number, tilesets), value);
      } catch {
        return false;
      }
    };
  }
  return null;
}

/**
 * The cells of a stroke that still hold what the stroke left, each turned back.
 *
 * @param cells The stroke's cells.
 * @param holds Whether a cell holds a value.
 * @returns The cells of the inverse stroke.
 */
function turnedBack<V extends FillCellValue>(cells: readonly { readonly index: number; readonly before: V; readonly after: V }[], holds: (index: number, value: FillCellValue) => boolean): { index: number; before: V; after: V }[] {
  return cells.filter((cell) => holds(cell.index, cell.after)).map((cell) => ({ index: cell.index, before: cell.after, after: cell.before }));
}

/**
 * Narrows a cell command to the cells that still hold what it left.
 *
 * @param world The document as it is now.
 * @param command The command as it applied.
 * @returns The inverse over those cells with the counts; no command when the layer is gone, holds another
 *   kind of cell, or none of the cells still holds its value.
 */
export function narrowedCells(world: WorldDocument, command: CellCommand): NarrowedCells {
  const holds = holder(layerOf(world, command.mapId, command.layerId), world.tilesets, command.kind);
  const total = command.kind === "fillCells" ? command.indices.length : command.cells.length;
  if (holds === null) {
    return { command: null, kept: 0, skipped: 0, gone: total };
  }
  if (command.kind === "fillCells") {
    let kept = 0;
    for (let i = 0; i < command.indices.length; i += 1) {
      kept += holds(command.indices[i] as number, command.after) ? 1 : 0;
    }
    const indices = new Uint32Array(kept);
    for (let i = 0, next = 0; i < command.indices.length; i += 1) {
      if (holds(command.indices[i] as number, command.after)) {
        indices[next] = command.indices[i] as number;
        next += 1;
      }
    }
    return { command: kept === 0 ? null : { ...command, indices, before: command.after, after: command.before }, kept, skipped: total - kept, gone: 0 };
  }
  const cells = turnedBack<FillCellValue>(command.cells, holds);
  return { command: cells.length === 0 ? null : ({ ...command, cells } as Command), kept: cells.length, skipped: total - cells.length, gone: 0 };
}
